import React, { useEffect, useState } from "react";
import { ShieldCheck, Play, Info } from "@phosphor-icons/react";

/**
 * GDPR-konformes Click-to-Load für eingebettete Iframes (Spotify, YouTube,
 * Apple Podcasts, …).
 *
 * Erst wenn der/die Nutzer:in aktiv klickt, wird der Iframe gerendert –
 * vorher fließt KEIN Pixel oder Cookie an Spotify / Google / Apple.
 *
 * Optional speichert der Wrapper die Zustimmung pro Anbieter in
 * `localStorage` (`bb.consent.<provider>`), sodass innerhalb derselben
 * Session nicht jedes Embed neu bestätigt werden muss.
 *
 * Props:
 *  - provider:    "youtube" | "spotify" | "apple" | string  (storage key)
 *  - title:       Anbieter-Name fürs UI (z. B. "YouTube")
 *  - description: Optionaler Erklärtext (Datenschutz-Hinweis)
 *  - aspect:      "video" | "audio" | "square" — bestimmt die Höhe
 *  - children:    Render-Funktion `() => <iframe …/>` ODER fertiges Element
 */
export default function ConsentEmbed({
  provider,
  title,
  description,
  aspect = "video",
  remember = true,
  children,
  ...rest
}) {
  const storageKey = `bb.consent.${provider}`;
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (remember) {
      try {
        if (localStorage.getItem(storageKey) === "1") setAccepted(true);
      } catch {
        /* ignore */
      }
    }
  }, [storageKey, remember]);

  const accept = () => {
    setAccepted(true);
    if (remember) {
      try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    }
  };

  if (accepted) {
    return typeof children === "function" ? children() : children;
  }

  const heightClass = {
    video: "aspect-video",
    audio: "h-[160px]",
    square: "aspect-square",
  }[aspect] || "aspect-video";

  const hostMap = {
    youtube: "youtube-nocookie.com (Google Ireland Ltd.)",
    spotify: "open.spotify.com (Spotify AB, Schweden)",
    apple: "embed.podcasts.apple.com (Apple Distribution Intl. Ltd.)",
  };
  const hostText = hostMap[provider] || provider;

  return (
    <div
      className={`${heightClass} w-full relative bg-[#F5F5F5] text-[#0A0A0A] rounded-sm overflow-hidden flex items-center justify-center`}
      data-testid={`consent-embed-${provider}${rest["data-testid-suffix"] ? `-${rest["data-testid-suffix"]}` : ""}`}
    >
      <div className="absolute inset-0 opacity-20"
        style={{
          background:
            "radial-gradient(circle at 80% 20%, #00FA9A 0%, transparent 50%), radial-gradient(circle at 10% 90%, #5E8C4A 0%, transparent 50%)",
        }}
      />
      <div className="relative z-10 px-5 py-6 max-w-md text-center">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#9FE8C4] bg-white/10 px-2 py-1 rounded-sm mb-3">
          <ShieldCheck size={12} weight="fill" /> DSGVO · Klick zum Laden
        </div>
        <h3 className="text-base md:text-lg font-medium tracking-tight">
          {title || "Externer Inhalt"}
        </h3>
        <p className="text-xs text-white/80 mt-2 leading-relaxed">
          {description ||
            `Wenn du auf „Anzeigen" klickst, lädt dein Browser den Inhalt direkt von ${hostText}. Dabei werden — je nach Anbieter — IP-Adresse, User-Agent und Cookies an Dritte übertragen.`}
        </p>
        <button
          onClick={accept}
          data-testid={`consent-accept-${provider}`}
          className="mt-4 inline-flex items-center gap-2 bg-[#00FA9A] hover:bg-[#33FBAE] text-white rounded-full px-4 py-2 text-sm font-medium"
        >
          <Play size={14} weight="fill" /> Inhalt anzeigen
        </button>
        {remember && (
          <div className="mt-3 inline-flex items-center gap-1 text-[10px] text-white/60">
            <Info size={10} /> Auswahl wird für diese Sitzung gemerkt
          </div>
        )}
      </div>
    </div>
  );
}
